import React, { useState, useEffect, useMemo } from 'react'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Badge } from 'primereact/badge'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from 'recharts'
import { _getTagHistories, _getAllAlertHistories } from '../../api/api'
import { PageHeader } from '../../../shared/PageHeader/PageHeader'
import { DataTableComponent } from '@omniyat/reactcomponents'
import { fetchConversationList, setChatInfo, setDetailChat } from '../../../../_metronic/partials/layout/drawer-messenger/slice/Chat.slice'
import { DrawerComponent } from '../../../../_metronic/assets/ts/components'
import { useDispatch } from 'react-redux'

const SensorDetail = ({ sensor, type, onBack }) => {
  const [loading, setLoading] = useState(false)
  const [chartData, setChartData] = useState([])
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [mode, setMode] = useState('day')
  
  // Alert histories state
  const [alertHistories, setAlertHistories] = useState([])
  const [loadingAlerts, setLoadingAlerts] = useState(false)


  const dispatch = useDispatch()
  const codeTemplate = (rowData) => (
    <Badge 
      value={rowData.alertCode || 'N/A'} 
      severity={rowData.isEnd === 0 ? 'danger' : 'success'}
    />
  )

  const messageTemplate = (rowData) => (
    <span title={rowData.message}>
      {rowData.message ? 
      (rowData.message.length > 60 ? 
        rowData.message.substring(0, 60) + '...' : 
        rowData.message
      ) : 'N/A'
      }
    </span>
  )

  const starDateTemplate = (rowData) => {
    if (rowData.startDate) {
      return new Date(rowData.startDate).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
      })
    }
    return 'N/A'
  }

  const endDateTemplate = (rowData) => {
    if (rowData.endDate && rowData.isEnd === 1) {
      return new Date(rowData.endDate).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
      })
    }
    return rowData.isEnd === 0 ? 'En cours' : 'N/A'
  }
          
  const commentTemplate = (rowData) => {
    const commentsCount = Array.isArray(rowData.comments) ? rowData.comments.length : 0
    return (
      <Badge 
      value={commentsCount > 0 ? `${commentsCount} intervention${commentsCount > 1 ? 's' : ''}` : 'Aucune'} 
      severity={commentsCount > 0 ? 'info' : 'secondary'}
      />
    )
  }
          
  const isReadTemplate = (rowData) => (
    <Badge 
      value={rowData.isRead === 1 ? 'Lu' : 'Non lu'} 
      severity={rowData.isRead === 1 ? 'success' : 'warning'}
    />
  )


  //columns
  let columns = useMemo(()=> [
      {
          "field": "alertCode",
          "header": "Code Alerte",
          body: codeTemplate,
          "style": {
              "width": "140px"
          }
      },
      {
          "field": "message",
          "header": "Message",
          body: messageTemplate
      },
      
      {
          "field": "startDate",
          "header": "Début",
          body: starDateTemplate,
          "style": {
              "width": "140px"
          }
      },
      {
          "field": "endDate",
          "header": "Fin",
          body: endDateTemplate ,
          "style": {
              "width": "140px"
          }
      },
      {
          "field": "comments",
          "header": "Interventions",
          body: commentTemplate,
          "style": {
              "width": "120px"
          }
      },
      {
          "field": "isRead",
          "header": "Statut",
          body: isReadTemplate,
          "style": {
              "width": "100px"
          }
      }
  ], [])

  
  // Generate date range based on mode
  const getDateRange = (date, mode) => {
    const start = new Date(date)
    const end = new Date(date)
    
    if (mode === 'day') {
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)
    } else if (mode === 'week') {
      const dayOfWeek = start.getDay()
      const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)
      start.setDate(diff)
      start.setHours(0, 0, 0, 0)
      end.setDate(start.getDate() + 6)
      end.setHours(23, 59, 59, 999)
    } else if (mode === 'month') {
      start.setDate(1)
      start.setHours(0, 0, 0, 0)
      end.setMonth(end.getMonth() + 1, 0)
      end.setHours(23, 59, 59, 999)
    }
    
    return {
      from: start.toISOString(),
      to: end.toISOString()
    }
  }

   const displayChatDetail = () => {
    let obj = {
      srcId: sensor.id,
      srcObject: 'Engin',
      label: sensor.label || sensor.code || sensor.reference
    }

    dispatch(setChatInfo(obj));
    dispatch(fetchConversationList(obj)).then((res) => {
      console.log('res fetchConversationList', res)
      dispatch(setDetailChat(true))
    })
    const drawer = DrawerComponent.getInstance('kt_drawer_chat')
    if (drawer) {
      drawer.show()
      return
    }

    // Fallback: trigger the DOM toggle (the one used in Navbar)
    const toggle = document.getElementById('kt_drawer_chat_toggle')
    if (toggle) {
      toggle.click()
    }
  }

  // Fetch alert histories for the sensor
  const fetchAlertHistories = async () => {
    if (!sensor) return
    
    setLoadingAlerts(true)
    
    try {
      // Use tagId or id from sensor data
      const sensorId = sensor.tagId || sensor.id
      
      console.log('🚨 SensorDetail: Chargement de l\'historique des alertes pour capteur:', sensor.name || sensor.label, 'tagId:', sensorId)
      
      // Use _getAllAlertHistories (alert/histories) - peut filtrer par tagId si nécessaire
      const response = await _getAllAlertHistories({
        // Peut inclure des filtres si nécessaire
        ...(sensorId && { tagId: sensorId })
      })
      
      console.log('🚨 SensorDetail alert histories response:', response)
      
      if (response.success && response.result && Array.isArray(response.result)) {
        // Filtrer les alertes pour ce capteur spécifique
        let filteredHistories = response.result
        
        // Utiliser l'adresse MAC du capteur pour le filtrage
        const sensorMac = sensor.name || sensor.label || sensor.macAddr || sensor.mac
        
        if (sensorMac || sensorId) {
          // Filtrer par tagId ou macAddr
          filteredHistories = response.result.filter(alert => 
            (sensorId && alert.tagId === String(sensorId)) ||
            (sensorMac && alert.macAddr === sensorMac)
          )
        }
        
        console.log('🚨 SensorDetail filtered histories:', filteredHistories.length, 'alertes trouvées pour capteur:', sensorMac || sensorId)
        setAlertHistories(filteredHistories)
      } else {
        console.log('⚠️ SensorDetail: No alert histories available')
        setAlertHistories([])
      }
    } catch (error) {
      console.error('🚨 SensorDetail alert histories error:', error)
      setAlertHistories([])
    } finally {
      setLoadingAlerts(false)
    }
  }

  // Fetch sensor data using the same logic as Courbe.tsx
  const fetchData = async () => {
    if (!sensor?.label) return
    
    setLoading(true)
    
    try {
      const dateRange = getDateRange(selectedDate, mode)
      
      console.log('📊 SensorDetail: Chargement de l\'historique pour:', sensor.label)
      
      // Use the exact same request structure as Courbe.tsx
      // Use same tag identification logic as SensorList for consistency
      const tagId = sensor.name || sensor.label || sensor.macAddr || sensor.mac || sensor.id
      
      const response = await _getTagHistories({
        tags: [tagId], // Use same tag identification as SensorList
        from: dateRange.from,
        to: dateRange.to
        // No group: 1 and no mode: 'all' parameters
      })
      
      console.log('📊 SensorDetail response:', response)
      
      if (response.success && response.response && Array.isArray(response.response)) {
        // Use the exact same data processing logic as Courbe.tsx
        const groupedData = {}
        
        response.response.forEach((item) => {
          const timestamp = item.datetimezone || item.timestamp || new Date().toISOString()
          
          if (!groupedData[timestamp]) {
            groupedData[timestamp] = {
              timestamp,
              temperature: 0,
              humidity: 0
            }
          }
          
          if (item.type === 'temperature') {
            groupedData[timestamp].temperature = parseFloat((item.mean || item.value || 0).toFixed(2))
          } else if (item.type === 'humidity' || item.type === 'humidity') {
            groupedData[timestamp].humidity = parseFloat((item.mean || item.value || 0).toFixed(2))
          }
        })
        
        // Convert to array and sort by timestamp (same as Courbe.tsx)
        const chartData = Object.values(groupedData)
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        
        console.log('📈 SensorDetail final chart data:', chartData.length, 'points')
        setChartData(chartData.map(item => ({
          datetimezone: item.timestamp,
          temperature: item.temperature,
          humidity: item.humidity
        })))
      } else {
        console.log('⚠️ SensorDetail: No data available for sensor:', sensor.label)
        setChartData([])
      }
    } catch (error) {
      console.error('🚨 SensorDetail fetch error:', error)
      setChartData([])
    } finally {
      setLoading(false)
    }
  }

  // Fetch data when dependencies change
  useEffect(() => {
    fetchData()
  }, [sensor, selectedDate, mode])
  
  // Fetch alert histories when sensor changes
  useEffect(() => {
    fetchAlertHistories()
  }, [sensor])

  // Navigate dates
  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate)
    if (mode === 'day') {
      newDate.setDate(newDate.getDate() + direction)
    } else if (mode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7))
    } else if (mode === 'month') {
      newDate.setMonth(newDate.getMonth() + direction)
    }
    setSelectedDate(newDate)
  }

  // Format display date
  const formatDate = () => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' }
    
    if (mode === 'day') {
      return selectedDate.toLocaleDateString('fr-FR', options)
    } else if (mode === 'week') {
      // Calculer le début et la fin de la semaine
      const start = new Date(selectedDate)
      const dayOfWeek = start.getDay()
      const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1) // Lundi = début de semaine
      start.setDate(diff)
      
      const end = new Date(start)
      end.setDate(start.getDate() + 6) // Dimanche = fin de semaine
      
      const startStr = start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
      const endStr = end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
      
      return `${startStr} - ${endStr}`
    } else if (mode === 'month') {
      return selectedDate.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })
    }
    
    return selectedDate.toLocaleDateString('fr-FR', options)
  }

  if (!sensor) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h3>Aucun capteur sélectionné</h3>
        <Button label="Retour" icon="pi pi-arrow-left" onClick={onBack} />
      </div>
    )
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* Page Header */}
      <PageHeader
        title="sensor.detail"
        onBack={onBack}
        info={
          <div className="flex align-items-center gap-3">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-id-card text-xl text-primary"></i>
              <span className="text-lg font-semibold">{sensor.label}</span>
            </div>
            {alertHistories.length > 0 && (
              <>
                <div className="border-left-2 border-300" style={{ height: '30px' }}></div>
                <div className="flex align-items-center gap-2">
                  <i className="pi pi-exclamation-triangle text-xl text-red-500"></i>
                  <span className="text-lg font-semibold text-red-600">
                    {alertHistories.length} alerte{alertHistories.length > 1 ? 's' : ''}
                  </span>
                </div>
              </>
            )}
            {
              <Button label='Communication' size="small" icon="pi pi-comment" onClick={displayChatDetail} />
            }
          </div>
        }
        className="mb-3"
      />

      {/* Alert Histories Section */}
      <div style={{
        marginBottom: '1.5rem',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        border: '1px solid #dee2e6',
        padding: '1rem'
      }}>
        {loadingAlerts ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem' }}></i>
            <p>Chargement des alertes...</p>
          </div>
        ) : alertHistories.length > 0 ? (
          <DataTableComponent
                tableId={'sensor-alert-history'}
                data={alertHistories}
                columns={columns}
                tableTitle={(
                  <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>
                    <i className="pi pi-exclamation-triangle mr-2" style={{ color: '#ffc107' }}></i>
                    Historique des Alertes
                  </h3>
                )}
                />
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
            <i className="pi pi-info-circle" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
            <p>Aucune alerte trouvée pour ce capteur</p>
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1.5rem',
        padding: '1rem',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        {/* Period selector */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {['day', 'week', 'month'].map(periodMode => (
            <Button
              key={periodMode}
              label={periodMode === 'day' ? 'Jour' : periodMode === 'week' ? 'Semaine' : 'Mois'}
              onClick={() => setMode(periodMode)}
              className={mode === periodMode ? 'p-button-primary' : 'p-button-outlined'}
              size="small"
            />
          ))}
        </div>
        
        {/* Date navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button 
            icon="pi pi-chevron-left" 
            onClick={() => navigateDate(-1)}
            className="p-button-outlined"
          />
          <span style={{ minWidth: '200px', textAlign: 'center', fontWeight: 'bold' }}>
            {formatDate()}
          </span>
          <Button 
            icon="pi pi-chevron-right" 
            onClick={() => navigateDate(1)}
            className="p-button-outlined"
          />
        </div>
      </div>

      {/* Chart */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        border: '1px solid #dee2e6',
        padding: '1.5rem',
        minHeight: '400px'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '1.5rem', textAlign: 'center' }}>
          Historique - {formatDate()}
        </h3>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
            <p>Chargement...</p>
          </div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={chartData}>
              <XAxis dataKey="datetimezone" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="temperature" 
                stroke="#ff6b6b" 
                name="Température (°C)"
                connectNulls={false}
              />
              <Line 
                type="monotone" 
                dataKey="humidity" 
                stroke="#4ecdc4" 
                name="Humidité (%)"
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p>Aucune donnée disponible</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default SensorDetail
